import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useCalendarTypes } from '../../../hooks/useCalendarTypes';
import { calendarService } from '../../../services/calendarService';
import { outlookPresetColors } from '../../CalendarRecentPanel/constants/calendarEventConstants';
import {
  CALENDAR_TYPE_GOOGLE,
  CALENDAR_TYPE_OUTLOOK,
  SCHEDULE_MEETING_NO_COLOR_OPTION,
} from '../helpers/constants';
import type { CalendarColorOption, CalendarOption } from '../types';

type GoogleColorsResponse = {
  event?: Record<string, { background?: string }>;
};

type MicrosoftCategory = {
  displayName?: string;
  color?: string;
};

const useCalendarColorOptions = (calendarOption: CalendarOption) => {
  const { data: accountConnections, isFetching: isRefreshingConnections } = useCalendarTypes();

  const { data: googleColors, isLoading: isGoogleColorsLoading } =
    useQuery<GoogleColorsResponse | null>({
      queryKey: ['scheduleMeetingGoogleColors', accountConnections?.google?.login_link],
      enabled: !!accountConnections?.google?.connected && !isRefreshingConnections,
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      queryFn: async () => {
        const accessToken = accountConnections?.google?.login_link;
        if (!accessToken) {
          return null;
        }
        return (await calendarService.fetchGoogleCalendarColors(
          accessToken,
        )) as GoogleColorsResponse | null;
      },
    });

  const { data: microsoftCategories, isLoading: isMicrosoftColorsLoading } = useQuery<
    MicrosoftCategory[]
  >({
    queryKey: ['scheduleMeetingMicrosoftCategories', accountConnections?.microsoft?.login_link],
    enabled: !!accountConnections?.microsoft?.connected && !isRefreshingConnections,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const accessToken = accountConnections?.microsoft?.login_link;
      if (!accessToken) {
        return [];
      }
      const categories = await calendarService.fetchMicrosoftCategories(accessToken);
      return (categories ?? []) as MicrosoftCategory[];
    },
  });

  const googleColorOptions = useMemo<CalendarColorOption[]>(() => {
    const eventColors = googleColors?.event ?? {};
    const fromApi = Object.entries(eventColors)
      .map(([id, value]) => ({
        id,
        label: `Google color ${id}`,
        hexColor: value?.background ?? '',
      }))
      .filter((option) => option.hexColor.length > 0);
    return [SCHEDULE_MEETING_NO_COLOR_OPTION, ...fromApi];
  }, [googleColors]);

  const microsoftColorOptions = useMemo<CalendarColorOption[]>(() => {
    const fromCategories = (microsoftCategories ?? [])
      .map((category) => {
        const label = category.displayName?.trim();
        if (!label) {
          return null;
        }

        const preset = category.color?.trim();
        if (!preset) {
          return null;
        }

        const hexColor = outlookPresetColors[preset];
        if (!hexColor) {
          return null;
        }

        return {
          id: label,
          label,
          hexColor,
        };
      })
      .filter((option): option is CalendarColorOption => option !== null);
    return [SCHEDULE_MEETING_NO_COLOR_OPTION, ...fromCategories];
  }, [microsoftCategories]);

  if (calendarOption === CALENDAR_TYPE_GOOGLE) {
    return {
      colorOptions: googleColorOptions,
      isLoading: isGoogleColorsLoading,
    };
  }

  if (calendarOption === CALENDAR_TYPE_OUTLOOK) {
    return {
      colorOptions: microsoftColorOptions,
      isLoading: isMicrosoftColorsLoading,
    };
  }

  return {
    colorOptions: [],
    isLoading: false,
  };
};

export default useCalendarColorOptions;
